import { type ReactNode } from 'react';
import { CheckCheck } from 'lucide-react';
import { DropdownMenuContent } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type NotificationDropdownContentProps = {
  dataCy: string;
  className?: string;
  children: ReactNode;
  // Optional: the footer renders only when passed.
  markAll?: {
    label: string;
    dataCy: string;
    disabled: boolean;
    onClick: () => void;
  };
};

/**
 * Shared dropdown panel for NotificationDropdown (authenticated + OTP guests):
 * the positioned content container and a scrollable body slot, plus an optional
 * "mark all as read" footer. The body stays a slot since it's backend-backed
 * with its own loading/error states; `className` carries the only chrome
 * difference (panel min-height).
 */
export const NotificationDropdownContent = ({
  dataCy,
  className,
  children,
  markAll,
}: NotificationDropdownContentProps) => (
  <DropdownMenuContent
    align="end"
    className={cn('flex w-96 flex-col space-y-px border-ui-void-gap! bg-ui-void-gap p-0', className)}
    sideOffset={6}
    alignOffset={-28}
    data-cy={dataCy}
  >
    <div className="flex grow flex-col overflow-y-auto">{children}</div>
    {markAll && (
      <div className="flex justify-end border-t border-t-toolbar-bevel bg-ui-base p-2">
        <Button
          variant="main-toolbar"
          size="sm"
          disabled={markAll.disabled}
          data-cy={markAll.dataCy}
          onClick={markAll.onClick}
          icon={CheckCheck}
          iconPosition="right"
          iconClassName="icon-5"
        >
          {markAll.label}
        </Button>
      </div>
    )}
  </DropdownMenuContent>
);
